import { SubsidiariesTotalsDto } from "api/actor";
import { LegalProtectionEligibilityResponse } from "api/risk";
import { useAsyncResource } from "back-office/shared/hooks/useAsyncResource";
import { loadSubsidiaries } from "features/new-actor/hooks/optim/useSubsidiariesSync";
import { emptyFinancialsFormValues } from "features/new-actor/mappers/optim/mapSubsidiaries";
import { LegalEntityFinancialsFormValues } from "features/new-actor/types/optim/legalEntityFinancialsFormValues";
import { SubsidiaryFormValues } from "features/new-actor/types/optim/subsidiaryFormValues";
import { useCallback, useMemo } from "react";
import { risksAPI } from "services/swagger-api/riskApi";

export interface LegalProtectionCompanyData {
  /** Accounting data of the company, empty when nothing has been declared yet. */
  financials: LegalEntityFinancialsFormValues;
  subsidiaries: SubsidiaryFormValues[];
  /** Cumulated figures of the back; null while nothing is loaded. */
  totals: SubsidiariesTotalsDto | null;
  /** Verdict of the back; null while nothing is loaded. */
  eligibility: LegalProtectionEligibilityResponse | null;
  isLoading: boolean;
  /** The subsidiaries and the accounting data could not be read. */
  subsidiariesError: boolean;
  /** The verdict could not be read. */
  eligibilityError: boolean;
  /** Reloads the subsidiaries, the accounting data and the verdict; called after every save. */
  refresh: () => Promise<void>;
}

/**
 * Everything the legal protection first step shows besides the company itself: its subsidiaries,
 * its accounting data and the eligibility verdict, loaded together and reloaded after each save.
 * Both loads are independent: one failing still shows the other, and each one has its own message.
 * Without a company, nothing is loaded.
 */
export const useLegalProtectionCompanyData = (
  riskId?: string,
  legalEntityId?: string
): LegalProtectionCompanyData => {
  const enabled = Boolean(riskId && legalEntityId);

  const fetchData = useCallback(async () => {
    const [subsidiariesResult, eligibilityResult] = await Promise.allSettled([
      loadSubsidiaries(legalEntityId!),
      risksAPI.getLegalProtectionEligibility({ risqueAssureId: riskId! })
    ]);

    if (subsidiariesResult.status === 'rejected') {
      console.error('Chargement des filiales impossible', subsidiariesResult.reason);
    }
    if (eligibilityResult.status === 'rejected') {
      console.error('Chargement de l\'éligibilité impossible', eligibilityResult.reason);
    }

    return {
      subsidiaries: subsidiariesResult.status === 'fulfilled' ? subsidiariesResult.value : null,
      eligibility: eligibilityResult.status === 'fulfilled' ? eligibilityResult.value : null,
      subsidiariesFailed: subsidiariesResult.status === 'rejected',
      eligibilityFailed: eligibilityResult.status === 'rejected'
    };
  }, [legalEntityId, riskId]);

  const { data, isLoading, error, reload } = useAsyncResource(fetchData, enabled);

  return useMemo(() => ({
    financials: data?.subsidiaries?.financials ?? emptyFinancialsFormValues(),
    subsidiaries: data?.subsidiaries?.subsidiaries ?? [],
    totals: data?.subsidiaries?.totals ?? null,
    eligibility: data?.eligibility ?? null,
    isLoading,
    // A failure of the whole load leaves no result at all: both messages are shown.
    subsidiariesError: Boolean(error) || Boolean(data?.subsidiariesFailed),
    eligibilityError: Boolean(error) || Boolean(data?.eligibilityFailed),
    refresh: reload
  }), [data, isLoading, error, reload]);
};
